import { useState, useEffect } from 'react';
import { useFormContext } from '../context/FormContext';
import { findFirstBlankQuestionStep } from '../utils/formHelpers';

export const useFormNavigation = (totalSteps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [animateDirection, setAnimateDirection] = useState('next-in');
  const [showReview, setShowReview] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [hasCompletedOnce, setHasCompletedOnce] = useState(false);
  const [isEditingFromReview, setIsEditingFromReview] = useState(false);
  const [initialNavDone, setInitialNavDone] = useState(false);
  const { formData, isLoading } = useFormContext();

  // Category info calculations
  const categorySteps = [6, 8, 4, 3, 4, 4, 6, 3, 4]; // Steps per category (Personal Info, Background, Industry, Learning, Environment, Social, Decision, Achievement, Adaptability)
  
  const getCurrentCategoryInfo = () => {
    let categoryIndex = 0;
    let questionInCategory = currentStep;
    
    // Skip the welcome screen (step 0)
    if (currentStep > 0) {
      let stepCount = 0;
      for (let i = 0; i < categorySteps.length; i++) {
        // Add 1 to stepCount for each category because the first real question is at step 1
        stepCount += categorySteps[i];
        if (currentStep <= stepCount) {
          categoryIndex = i;
          // Calculate question number within the category (1-based)
          questionInCategory = currentStep - (stepCount - categorySteps[i]);
          break;
        }
      }
    }
    
    return {
      categoryIndex,
      questionInCategory,
      totalQuestionsInCategory: categorySteps[categoryIndex] || 0
    };
  };
  
  // Jump to first blank question if we're just starting
  useEffect(() => {
    if (!isLoading && !initialNavDone && Object.keys(formData).length > 0 && currentStep <= 1 && !showReview) {
      const firstBlankStep = findFirstBlankQuestionStep(formData);
      if (firstBlankStep > 1) {
        console.log(`Auto-navigating to first blank question: ${firstBlankStep}`);
        setCurrentStep(firstBlankStep);
      }
      setInitialNavDone(true);
    }
  }, [formData, isLoading, currentStep, showReview, initialNavDone]);
  
  // Calculate overall progress percentage
  const progress = ((currentStep) / totalSteps) * 100;
  
  // Handle navigation
  const nextStep = () => {
    if (currentStep < totalSteps) {
      setAnimateDirection('next-out');
      setTimeout(() => {
        setCurrentStep(prev => prev + 1);
        // Reset animation direction after content changes
        setTimeout(() => {
          setAnimateDirection('next-in');
        }, 50);
      }, 300);
    } else {
      setHasCompletedOnce(true);
      setShowReview(true);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setAnimateDirection('prev-out');
      setTimeout(() => {
        setCurrentStep(prev => prev - 1);
        // Reset animation direction after content changes
        setTimeout(() => {
          setAnimateDirection('prev-in');
        }, 50);
      }, 300);
    }
  };
  
  // Handle direct navigation to review page
  const backToReview = () => {
    setAnimateDirection('next-out');
    setTimeout(() => {
      setShowReview(true);
      setIsEditingFromReview(false);
      // Reset animation direction after content changes
      setTimeout(() => {
        setAnimateDirection('next-in');
      }, 50);
    }, 300);
  };
  
  // Handle direct navigation to a specific question
  const goToQuestion = (index) => {
    // Calculate the actual step number based on category structure
    let targetStep = 0;
    let questionCount = 0;
    let foundStep = false;
    
    // Skip welcome screen
    for (let catIndex = 0; catIndex < categorySteps.length; catIndex++) {
      for (let qIndex = 0; qIndex < categorySteps[catIndex]; qIndex++) {
        questionCount++;
        if (questionCount - 1 === index) {  // -1 because index is 0-based
          targetStep = questionCount;  // +1 because steps start at 1 (0 is welcome)
          foundStep = true;
          break;
        }
      }
      if (foundStep) break;
    }
    
    if (targetStep > 0) {
      setAnimateDirection('next-out');
      setTimeout(() => {
        setCurrentStep(targetStep);
        setShowReview(false);
        setIsEditingFromReview(true);
        // Reset animation direction after content changes
        setTimeout(() => {
          setAnimateDirection('next-in');
        }, 50);
      }, 300);
    }
  };

  const handleSubmit = () => {
    // Animation for submission
    setSubmitted(true);
    // Here you would typically send data to your backend
    console.log("Form submitted");
  };

  // Handle navigation to a specific category
  const navigateToCategory = (categoryIndex) => {
    // Calculate the step to navigate to (first question of the selected category)
    let targetStep = 1; // Start after welcome screen
    
    // Add up all questions in categories before the target one
    for (let i = 0; i < categoryIndex; i++) {
      targetStep += categorySteps[i];
    }
    
    // Navigate to that step
    setAnimateDirection('next-out');
    setTimeout(() => {
      setCurrentStep(targetStep);
      setTimeout(() => {
        setAnimateDirection('next-in');
      }, 50);
    }, 300);
  };
  
  // Find the first blank question and jump to it
  const jumpToFirstBlankQuestion = () => {
    if (Object.keys(formData).length > 0) {
      const firstBlankStep = findFirstBlankQuestionStep(formData);
      
      setAnimateDirection('next-out');
      setTimeout(() => {
        setCurrentStep(firstBlankStep);
        setTimeout(() => {
          setAnimateDirection('next-in');
        }, 50);
      }, 300);
      
      return true;
    }
    return false;
  };

  return {
    currentStep,
    animateDirection,
    showReview,
    submitted,
    hasCompletedOnce,
    isEditingFromReview,
    progress,
    categoryInfo: getCurrentCategoryInfo(),
    categorySteps,
    nextStep,
    prevStep,
    backToReview,
    goToQuestion,
    handleSubmit,
    setShowReview,
    setSubmitted,
    setCurrentStep,
    navigateToCategory,
    jumpToFirstBlankQuestion
  };
};